import {Accordion, Col, Container, Row} from "react-bootstrap";
import React, {useEffect} from "react";
import {useDispatch} from "react-redux";
import {useAppSelector} from "../../app/store";
import {useNavigate, useParams} from "react-router-dom";
import {getAdminSession} from "../../app/slices/adminSession";
import AdminLoader from "./AdminLoader";
import toast from "react-hot-toast";
import {clearError} from "../../app/slices/gameSession";
import {ADMIN_LANG} from "../../settings";
import {formatDateTime, formatDuration} from "./utils";
import {SessionContent, SessionSection} from "../../types/admin";
import {CheckCircleFill, XCircleFill} from "react-bootstrap-icons";

const SessionView = () => {
    const dispatch = useDispatch();
    const {
        loaded,
        error,
        duration,
        points,
        max_points,
        date_start,
        date_end,
        passed,
        sections,
        form_fields,
        game,
        game_id,
        game_version_id,
        user_id,
        state,
        live,
        teams
    } = useAppSelector(state => state.adminSession);

    const {session_id} = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        // NOTE: on React.StrictMode, this useEffect is called twice on dev mode
        if (session_id) {
            dispatch(getAdminSession(session_id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session_id]);

    if (error) {
        toast.error(error, {position: 'top-right',});
        dispatch(clearError());
    }

    return (
        <Container className='pt-4 pb-5'>
            {!loaded && <AdminLoader/>}
            <Row className='gy-4'>
                <Col xs={12}>
                    <button type="button" className="me-2 btn btn-primary" onClick={() => navigate(-1)}>
                        Takaisin
                    </button>
                    {game_id && (
                        <a className="me-2 btn btn-outline-primary"
                           href={`/admin/games/${game_id}/versions/${game_version_id}`}>
                            Pelin tiedot
                        </a>
                    )}
                    {user_id && (
                        <a type="button" className="me-2 btn btn-outline-primary" href={`/admin/users/${user_id}`}>
                            Pelaajan tiedot
                        </a>
                    )}
                </Col>
                <Col xs={12}>
                    <h2 className="mb-0">Session tiedot</h2>
                </Col>
                <Col xs={12}>
                    <div className='admin-card small-p-margin'>
                        <p>
                            <strong>{ADMIN_LANG.game}:</strong> <a
                            href={`/admin/games/${game_id}/versions/${game_version_id}`}>{game}</a>
                        </p>
                        <p>
                            <strong>{ADMIN_LANG.points}:</strong> {points} / {max_points}
                        </p>
                        {date_end && (
                            <p>
                                <strong>{ADMIN_LANG.passed}:</strong> {passed ?
                                <CheckCircleFill className="text-success"/> :
                                <XCircleFill className="text-danger"/>}<br/>
                            </p>
                        )}
                        {date_start && (
                            <p>
                                <strong>{ADMIN_LANG.started}:</strong> {formatDateTime(date_start)}<br/>
                            </p>
                        )}
                        {date_end && (
                            <p>
                                <strong>{ADMIN_LANG.ended}:</strong> {formatDateTime(date_end)}<br/>
                            </p>
                        )}
                        {duration && (
                            <p>
                                <strong>{ADMIN_LANG.duration}:</strong> {formatDuration(duration)}<br/>
                            </p>
                        )}
                        {teams && teams.length > 0 && (
                            <p>
                                <strong>{ADMIN_LANG.teams}:</strong> {teams.map((team) => (
                                <span key={team.name} className="badge bg-secondary me-1">{team.name}</span>
                            ))}
                            </p>
                        )}
                        <p>
                            <strong>{ADMIN_LANG.state}:</strong> {state}
                            {live && (
                                <>
                                    &nbsp;<span className='badge bg-primary'>Live</span>
                                </>
                            )}
                        </p>
                    </div>
                </Col>
                {form_fields.length > 0 && (
                    <Col xs={12}>
                        <div className='admin-card small-p-margin'>
                            {form_fields.map((field) => (
                                field.value && (
                                    <p>
                                        <strong key={field.key}>{field.label}:</strong> {field.value}<br/>
                                    </p>
                                )
                            ))}
                        </div>
                    </Col>
                )}
                <Col xs={12}>
                    <div className='accordion-header-row'>
                        <Row>
                            <Col xs={6}>
                                {ADMIN_LANG.name}
                            </Col>
                            <Col xs={2}>
                                {ADMIN_LANG.points}
                            </Col>
                            <Col xs={2}>
                                {ADMIN_LANG.duration}
                            </Col>
                            <Col xs={2}>
                                {ADMIN_LANG.pages}
                            </Col>
                        </Row>
                    </div>
                    <Accordion className='session-accordion section-container' defaultActiveKey={['0']} alwaysOpen>
                        {sections.map((section: SessionSection, index) => (
                            <Accordion.Item eventKey={index.toString()} key={section.name}>
                                <Accordion.Header>
                                    <Row className='row'>
                                        <Col xs={6}><span
                                            className='badge bg-light text-dark me-1'>#{index + 1}</span> {section.name}
                                        </Col>
                                        <Col xs={2}>
                                            {section.points || section.max_points ? (
                                                <>
                                                    {section.points || 0} / {section.max_points || 0}
                                                </>
                                            ) : (
                                                <>
                                                    -
                                                </>
                                            )}
                                        </Col>
                                        <Col xs={2}>{section.duration && formatDuration(section.duration)}</Col>
                                        <Col xs={2}>{section.contents.length}</Col>
                                    </Row>
                                </Accordion.Header>
                                <Accordion.Body className='session-accordion-body'>
                                    <Row className='session-accordion-header'>
                                        <Col xs={8}>{ADMIN_LANG.content}</Col>
                                        <Col xs={2}>{ADMIN_LANG.points}</Col>
                                        <Col xs={2}>{ADMIN_LANG.duration}</Col>
                                    </Row>
                                    {section.contents.map((content) => (
                                        <Row className='session-accordion-details' key={content.key}>
                                            <Col xs={8}>
                                                <p>{content.title}
                                                    <span
                                                        className={`ms-2 badge ${content.type === 'slide' ? 'bg-secondary' : 'bg-info'}`}>
                                                        <span>{content.badge}</span>
                                                    </span>

                                                </p>
                                                {renderAnswer(content)}
                                            </Col>
                                            <Col xs={2}>
                                                {content.date_end && (content.points || content.max_points) ? (
                                                    <>
                                                        {content.points || 0} / {content.max_points || 0}
                                                    </>
                                                ) : (
                                                    <></>
                                                )}
                                            </Col>
                                            <Col xs={2}>{content.duration && formatDuration(content.duration)}</Col>
                                        </Row>
                                    ))}
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </Col>
            </Row>
        </Container>
    )
}


const renderAnswer = (content: SessionContent) => {

    if (!content.answers || content.answers.length === 0) {
        return;
    }

    switch (content.question_type) {
        case 'order':
            return (
                <div className='answer order'>
                    <div className="breadcrumb">
                        <span className='me-1'>{ADMIN_LANG.answered}: </span>
                        {content.answers.map((answer) => (
                            <div
                                className={`breadcrumb-item ${content.points === content.max_points ? 'correct' : 'incorrect'}`}
                                key={answer.key}>
                                {answer.option}
                            </div>
                        ))}
                    </div>
                </div>
            );
        case 'select':
            if (content.answers.length > 1) {
                return (
                    <div className='answer multiple'>
                        <div className="breadcrumb">
                            <span className='me-1'>{ADMIN_LANG.answered}: </span>
                            {content.answers.map((answer) => (
                                <div
                                    className={`breadcrumb-item ${answer.points > 0 ? 'correct' : 'incorrect'}`}
                                    key={answer.key}
                                >
                                    {answer.option} ({answer.points})
                                </div>
                            ))}
                        </div>
                    </div>
                );
            } else {
                return (
                    <p className='answer'>
                        {ADMIN_LANG.answered}: <span
                        className={content.points > 0 ? 'correct' : 'incorrect'}>{content.answers[0]?.option} ({content.points})</span>
                    </p>
                );
            }
        case 'connect':
            return (
                <div className='answer connect'>
                    {content.answers.map((answer, index) => (
                        <div className="breadcrumb" key={`${answer.key}-${index}`}>
                            <span className='me-1'>{ADMIN_LANG.connected}: </span>
                            <div
                                className={`breadcrumb-item ${content.points === content.max_points ? 'correct' : ''}`}
                            >
                                {answer.option}
                            </div>
                            <div
                                className={`breadcrumb-item ${content.points === content.max_points ? 'correct' : ''}`}
                            >
                                {answer.option2}
                            </div>
                        </div>
                    ))}
                </div>
            );
        case 'text':
            return (
                <div className='answer'>
                    {content.answers.map((answer) => (
                        <div key={answer.key}>
                            <em>{answer.question}</em> » {answer.answer}
                        </div>
                    ))}
                </div>
            );
        case 'number_scale':
            return (
                <p className='answer'>
                    {ADMIN_LANG.answered}: {content.answers[0]?.rating}
                </p>
            );
        default:
            return;
    }
};

export default SessionView;